import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'wouter';
import { FileText, Package, TrendingUp, Settings, Home, ArrowLeft, Layout, FolderOpen, Menu, LogOut, User, CreditCard, HelpCircle, MessageSquare, RefreshCw, Mail, BarChart3, Users } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface AdminNavigationProps {
  currentPage?: string;
  showBackButton?: boolean;
}

export default function AdminNavigation({ currentPage, showBackButton = false }: AdminNavigationProps) {
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();

  const getUserInitials = () => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getPageDisplayName = (pageName: string) => {
    const abbreviations: { [key: string]: string } = {
      'Content Management System': 'CMS',
      'Management': 'Manage',
      'Support & Documentation': 'Support',
      'Pricing Intelligence': 'Pricing',
      'AI Assistant': 'AI Chat',
      'Stripe Sync': 'Sync',
      'Merchandise': 'Merch',
      'Reports': 'Reports',
      'Users': 'Users',
      'Payments': 'Payments'
    };
    
    return abbreviations[pageName] || pageName;
  };

  const navItems = [
    {
      href: '/admin',
      icon: Home,
      label: 'Overview',
      shortLabel: 'Home'
    },
    {
      href: '/admin/cms',
      icon: Layout,
      label: 'Content',
      shortLabel: 'CMS'
    },
    {
      href: '/admin/users',
      icon: User,
      label: 'Users',
      shortLabel: 'Users'
    },
    {
      href: '/admin/influencers',
      icon: Users,
      label: 'Influencers',
      shortLabel: 'Influencers'
    },
    {
      href: '/admin/dashboard',
      icon: Settings,
      label: 'Management',
      shortLabel: 'Manage'
    },
    {
      href: '/admin/payments',
      icon: CreditCard,
      label: 'Payments',
      shortLabel: 'Payments'
    },
    {
      href: '/admin/stripe-sync',
      icon: RefreshCw,
      label: 'Stripe Sync',
      shortLabel: 'Sync'
    },
    {
      href: '/admin/reports',
      icon: FileText,
      label: 'Reports',
      shortLabel: 'Reports'
    },
    {
      href: '/admin/merchandise',
      icon: Package,
      label: 'Merchandise',
      shortLabel: 'Merch'
    },
    {
      href: '/admin/pricing-intelligence',
      icon: TrendingUp,
      label: 'Pricing',
      shortLabel: 'Pricing'
    },
    {
      href: '/admin/ai-chat',
      icon: MessageSquare,
      label: 'AI Assistant',
      shortLabel: 'AI Chat'
    },
    {
      href: '/admin/email-templates',
      icon: Mail,
      label: 'Email Templates',
      shortLabel: 'Templates'
    },
    {
      href: '/admin/email-sequences',
      icon: BarChart3,
      label: 'Email Sequences',
      shortLabel: 'Sequences'
    },
    {
      href: '/admin/support',
      icon: HelpCircle,
      label: 'Support',
      shortLabel: 'Support'
    }
  ];

  const isActive = (href: string) => {
    if (href === '/admin') {
      return location === '/admin';
    }
    return location.startsWith(href);
  };

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-primary/10 sticky top-0 z-50 shadow-sm" role="navigation" aria-label="Admin navigation">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Page Title */}
          <div className="flex items-center space-x-2">
            {showBackButton && (
              <Link href="/admin">
                <Button variant="ghost" size="sm" className="lg:hidden hover:bg-primary/10">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
            )}
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-teal-600 flex items-center justify-center shadow-md">
              <Settings className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-sm md:text-base font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
                {getPageDisplayName(currentPage || 'Admin Panel')}
              </h1>
            </div>
          </div>

          {/* Desktop Navigation - Full Labels */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <Link key={item.href} href={item.href}>
                  <Button 
                    variant={active ? "default" : "ghost"} 
                    size="sm" 
                    className={`transition-all duration-200 ${
                      active 
                        ? "bg-gradient-to-r from-purple-600 to-teal-600 text-white shadow-md hover:shadow-lg" 
                        : "hover:bg-primary/10"
                    }`}
                    data-testid={`admin-nav-${item.shortLabel.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    <span>{item.label}</span>
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* Tablet Navigation - Icons Only */}
          <div className="hidden md:flex lg:hidden items-center space-x-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              
              return (
                <Link key={item.href} href={item.href}>
                  <Button 
                    variant={active ? "default" : "ghost"} 
                    size="sm" 
                    className={`p-2 transition-all duration-200 ${
                      active 
                        ? "bg-gradient-to-r from-purple-600 to-teal-600 text-white shadow-md" 
                        : "hover:bg-primary/10"
                    }`}
                    title={item.label}
                    aria-label={item.label}
                    data-testid={`admin-nav-${item.shortLabel.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                </Link>
              );
            })}
          </div>

          {/* User Menu and Mobile Navigation */}
          <div className="flex items-center space-x-2">
            {/* User Avatar */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-8 w-8 rounded-full" aria-label="User menu">
                  <Avatar className="h-8 w-8">
                    {user?.profilePictureUrl && (
                      <AvatarImage 
                        src={user.profilePictureUrl} 
                        alt={`${user?.firstName || 'User'}'s profile picture`}
                      />
                    )}
                    <AvatarFallback className="bg-gradient-to-br from-purple-600 to-teal-600 text-white text-xs">
                      {getUserInitials() || "A"}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border border-gray-200 shadow-lg" align="end">
                <div className="flex flex-col space-y-1 p-2 border-b border-gray-100">
                  <p className="text-sm font-medium leading-none text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs leading-none text-gray-500">
                    {user?.email}
                  </p>
                  <p className="text-xs leading-none text-purple-600 font-medium">
                    Administrator
                  </p>
                </div>
                <div className="p-1">
                  <DropdownMenuItem asChild>
                    <Link href="/profile" className="flex items-center cursor-pointer px-2 py-1.5 text-sm hover:bg-gray-100 rounded">
                      <User className="mr-2 h-4 w-4" />
                      <span>Back to Main App</span>
                    </Link>
                  </DropdownMenuItem>
                </div>
                <DropdownMenuSeparator />
                <div className="p-1">
                  <DropdownMenuItem
                    className="flex items-center cursor-pointer px-2 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                    onClick={() => logoutMutation.mutate()}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Hamburger Menu */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" aria-label="Mobile navigation menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64 bg-white border border-gray-200 shadow-lg" align="end">
                  <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-teal-50">
                    <p className="text-sm font-medium text-gray-900">Admin Panel</p>
                    <p className="text-xs text-gray-500">System Management</p>
                  </div>
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        <Link 
                          href={item.href} 
                          className={`flex items-center cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 rounded ${
                            active ? 'bg-purple-50 text-purple-700 font-medium' : 'text-gray-700'
                          }`}
                          data-testid={`admin-nav-mobile-${item.shortLabel.toLowerCase()}`}
                        >
                          <Icon className="mr-3 h-4 w-4" />
                          <span>{item.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator className="my-1" />
                  <DropdownMenuItem asChild>
                    <Link href="/home" className="flex items-center cursor-pointer px-3 py-2 text-sm hover:bg-gray-100 rounded text-gray-700">
                      <Home className="mr-3 h-4 w-4" />
                      <span>Back to Main App</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="flex items-center cursor-pointer px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
                    onClick={() => logoutMutation.mutate()}
                  >
                    <LogOut className="mr-3 h-4 w-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}